import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type WarehouseLocationsFormSchema,
	warehouseLocationsFormSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { WarehouseLocationsForm } from "@/forms/warehouse-locations-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface AddWarehouseLocationsModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
}

export function AddWarehouseLocationsModal({
	onClose,
	isOpen,
	onComplete,
}: AddWarehouseLocationsModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["warehouse-locations"].sizes.$get();
		const data = await responseHandler(response);
		return data.sizes;
	});
	const methods = useForm({
		resolver: resolver(warehouseLocationsFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: WarehouseLocationsFormSchema) {
		const response = await client.stock["warehouse-locations"].$patch({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.warehouse_locations")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<WarehouseLocationsForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
